import { useState } from 'react';
import { Loader2, Play, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

export function SupplierCostLineagePanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [period, setPeriod] = useState('ytd');

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.brosSupplierCostLineage(period);
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const bl = result?.booking_leg;
  const il = result?.bills_leg;

  return (
    <Card data-testid="supplier-cost-lineage-card">
      <CardHeader>
        <CardTitle className="text-lg">Supplier Cost Lineage</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Traces both supplier cost figures side by side: the booking COGS
          (supplier_total via compute_bros_turnover) and the supplier bills
          (bros_invoices type=supplier). Identifies the dominant cause of any
          discrepancy: currency face value, unpopulated supplier_total,
          double-count, or wrong source.
        </p>

        <div className="flex items-center gap-2">
          <div className="flex gap-0.5 bg-muted rounded-md p-0.5">
            {['ytd', 'last_12m', 'all'].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                  period === p
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {p === 'ytd' ? 'YTD' : p === 'last_12m' ? 'Last 12M' : 'All'}
              </button>
            ))}
          </div>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            Run
          </Button>
        </div>

        {result && (
          <div className="space-y-4" data-testid="lineage-result">
            {/* Verdict banner */}
            <div className={`flex items-start gap-2 text-sm rounded-md px-3 py-2 border ${
              result.verdict?.includes('FACE VALUE') || result.verdict?.includes('DOUBLE COUNT') || result.verdict?.includes('EMPTY')
                ? 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800'
                : 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800'
            }`}>
              {result.verdict?.includes('FACE VALUE') || result.verdict?.includes('DOUBLE COUNT') || result.verdict?.includes('EMPTY')
                ? <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                : <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
              }
              <div className="space-y-1" data-testid="lineage-verdict">
                {result.verdict?.split(' | ').map((part, i) => (
                  <p key={i}>{part}</p>
                ))}
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              {result.period_label} ({result.date_range?.start} to {result.date_range?.end})
            </p>

            {/* Side by side legs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Booking leg (expected) */}
              <div className="border rounded-lg p-3 space-y-1" data-testid="booking-leg">
                <p className="text-sm font-medium">Supplier Costs (Expected)</p>
                <p className="text-[10px] text-muted-foreground">
                  Source: {bl?.source}
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                  <span className="text-muted-foreground">Bookings</span>
                  <span className="tabular-nums">{fmtInt(bl?.booking_count)}</span>
                  <span className="text-muted-foreground">With supplier cost</span>
                  <span className="tabular-nums">{fmtInt(bl?.nonzero_supplier_total_count)}</span>
                  <span className="text-muted-foreground">Zero/missing</span>
                  <span className="tabular-nums text-amber-600">{fmtInt(bl?.zero_supplier_total_count)}</span>
                  <span className="text-muted-foreground">Populated %</span>
                  <span className="tabular-nums">
                    {bl?.populated_pct != null ? `${bl.populated_pct}%` : '-'}
                  </span>
                  <span className="text-muted-foreground font-medium">Total COGS (GBP)</span>
                  <span className="tabular-nums font-medium">{fmt(bl?.total_cogs_gbp)}</span>
                  <span className="text-muted-foreground">Total Turnover (GBP)</span>
                  <span className="tabular-nums">{fmt(bl?.total_turnover_gbp)}</span>
                </div>
                {bl?.booking_currency_counts && Object.keys(bl.booking_currency_counts).length > 1 && (
                  <div className="mt-2">
                    <p className="text-[10px] text-muted-foreground font-medium">By Currency</p>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {Object.entries(bl.booking_currency_counts).map(([cur, cnt]) => (
                        <Badge key={cur} variant="outline" className="text-[10px] px-1.5 py-0">
                          {cur}: {cnt}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Bills leg (actual) */}
              <div className="border rounded-lg p-3 space-y-1" data-testid="bills-leg">
                <p className="text-sm font-medium">Supplier Bills (Actual)</p>
                <p className="text-[10px] text-muted-foreground">
                  Source: {il?.source}
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                  <span className="text-muted-foreground">Invoice count</span>
                  <span className="tabular-nums">{fmtInt(il?.invoice_count)}</span>
                  <span className="text-muted-foreground font-medium">Total Bills (GBP)</span>
                  <span className="tabular-nums font-medium">{fmt(il?.total_bills_gbp)}</span>
                  <span className="text-muted-foreground">Total (native, mixed)</span>
                  <span className="tabular-nums">{fmt(il?.total_bills_native_mixed)}</span>
                </div>

                {/* Currency breakdown table */}
                {il?.currency_breakdown?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-[10px] text-muted-foreground font-medium mb-1">By Currency</p>
                    <div className="border rounded overflow-x-auto">
                      <table className="w-full text-xs" data-testid="currency-breakdown-table">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left px-2 py-1 font-medium">Currency</th>
                            <th className="text-right px-2 py-1 font-medium">Count</th>
                            <th className="text-right px-2 py-1 font-medium">Native</th>
                            <th className="text-right px-2 py-1 font-medium">Rate</th>
                            <th className="text-right px-2 py-1 font-medium">GBP</th>
                          </tr>
                        </thead>
                        <tbody>
                          {il.currency_breakdown.map((row) => (
                            <tr key={row.currency} className={`border-b last:border-0 ${row.is_face_value ? 'bg-red-50 dark:bg-red-950/20' : ''}`}>
                              <td className="px-2 py-1">
                                <Badge variant={row.is_face_value ? 'destructive' : 'outline'} className="text-[10px] px-1.5 py-0">
                                  {row.currency}
                                </Badge>
                              </td>
                              <td className="px-2 py-1 text-right tabular-nums">{row.count}</td>
                              <td className="px-2 py-1 text-right tabular-nums">{fmt(row.native_total)}</td>
                              <td className="px-2 py-1 text-right tabular-nums">{row.rate_to_gbp}</td>
                              <td className="px-2 py-1 text-right tabular-nums">{fmt(row.gbp_total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Source breakdown */}
                {il?.source_breakdown?.length > 1 && (
                  <div className="mt-2">
                    <p className="text-[10px] text-muted-foreground font-medium mb-1">By Source</p>
                    <div className="flex flex-wrap gap-2">
                      {il.source_breakdown.map((s) => (
                        <div key={s.source} className="border rounded px-2 py-1 text-xs">
                          <span className="font-medium">{s.source}</span>: {s.count} invoices, GBP {fmt(s.gbp_total)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Face value warning */}
                {il?.face_value_currencies?.length > 0 && (
                  <div className="mt-2 flex items-start gap-1.5 text-xs text-red-600 bg-red-50 dark:bg-red-950/20 rounded px-2 py-1.5 border border-red-200 dark:border-red-800"
                       data-testid="face-value-warning">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>
                      {il.face_value_currencies.map(c => c.currency).join(', ')} invoices summed at
                      face value (no FX rate). This is the likely cause of overstatement.
                    </span>
                  </div>
                )}

                {/* Twin duplicates */}
                {il?.twin_duplicate_count > 0 && (
                  <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/20 rounded px-2 py-1.5 border border-amber-200 dark:border-amber-800"
                       data-testid="twin-duplicate-warning">
                    <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>
                      {il.twin_duplicate_count} booking(s) have invoices from multiple sources,
                      potentially double-counting.
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* FX rates used */}
            {result.fx_rates_used && (
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer hover:text-foreground">FX rates used</summary>
                <div className="flex flex-wrap gap-2 mt-1">
                  {Object.entries(result.fx_rates_used).map(([cur, rate]) => (
                    <span key={cur} className="font-mono bg-muted px-1.5 py-0.5 rounded">{cur}: {rate}</span>
                  ))}
                </div>
              </details>
            )}

            <p className="text-[10px] text-muted-foreground">
              Generated: {result.generated_at}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
